import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { CreateObjectDialog } from './CreateObjectDialog'

export type CreatableObject =
  | 'advertiser'
  | 'order'
  | 'lineItem'
  | 'creative'
  | 'earningsAccount'
  | 'site'
  | 'siteGroup'
  | 'adUnit'
  | 'blocklist'
  | 'ruleset'

/** Objects created in a modal. Creatives keep their own bulk upload page. */
export type DialogCreatableObject = Exclude<CreatableObject, 'creative'>

/** Parents the new object is created under — whatever the list already knows. */
export interface CreateObjectContext {
  advertiser?: string
  team?: string
  order?: string
  lineItem?: string
  earningsAccount?: string
  site?: string
}

const CONFIG: Record<
  CreatableObject,
  {
    path: string
    label: string
    params: (keyof CreateObjectContext)[]
    idLength?: number
    /** A page that creates many at once, used in place of the single edit page. */
    bulkPath?: string
  }
> = {
  advertiser: { path: '/advertising/advertisers', label: 'advertisers.newAdvertiser', params: [] },
  order: { path: '/advertising/orders', label: 'orders.newOrder', params: ['advertiser', 'team'] },
  lineItem: { path: '/advertising/lineitems', label: 'lineItems.newLineItem', params: ['order', 'advertiser', 'team'] },
  creative: {
    path: '/advertising/creatives',
    label: 'creatives.newCreative',
    params: ['lineItem', 'advertiser'],
    bulkPath: '/advertising/creatives/upload',
  },
  earningsAccount: { path: '/publishing/earnings', label: 'publishers.newPublisher', params: [], idLength: 12 },
  site: { path: '/publishing/sites', label: 'sites.newSite', params: ['earningsAccount'], idLength: 12 },
  siteGroup: { path: '/publishing/site-groups', label: 'siteGroups.newSiteGroup', params: [] },
  adUnit: { path: '/publishing/ad-units', label: 'adUnits.newAdUnit', params: ['site'] },
  blocklist: { path: '/publishing/blocklists', label: 'blocklists.newBlocklist', params: [] },
  ruleset: { path: '/publishing/rulesets', label: 'rulesets.newRuleset', params: [] },
}

/** Query-param names the edit pages read their parents from. */
const PARAM_NAME: Record<keyof CreateObjectContext, string> = {
  advertiser: 'advertiser',
  team: 'team',
  order: 'order',
  lineItem: 'lineitem',
  earningsAccount: 'earningsAccount',
  site: 'site',
}

/**
 * Builds the URL of a new, unsaved object's edit page — or of its bulk page,
 * where it has one (creatives are uploaded in bulk), unless `single` asks for
 * the edit page.
 */
export function newObjectUrl(
  type: CreatableObject,
  context: CreateObjectContext = {},
  { single = false }: { single?: boolean } = {}
): string {
  const { path, params: keys, idLength = 16, bulkPath } = CONFIG[type]
  const params = new URLSearchParams()
  for (const key of keys) {
    const value = context[key]
    if (value) params.set(PARAM_NAME[key], value)
  }
  if (bulkPath && !single) {
    const query = params.toString()
    return query ? `${bulkPath}?${query}` : bulkPath
  }
  params.set('new', 'true')
  const id = crypto.randomUUID().replace(/-/g, '').slice(0, idLength)
  return `${path}/${id}?${params}`
}

export interface CreateObjectButtonProps {
  type: CreatableObject
  context?: CreateObjectContext
  size?: 'sm' | 'default'
  variant?: 'default' | 'outline'
}

/**
 * "New …" button for any list of creatable objects. Passes the list's parents
 * on, so the new object starts inside them, and opens its create form in a
 * modal — or, for creatives, goes to the bulk upload page.
 */
export function CreateObjectButton({ type, context, size = 'sm', variant }: CreateObjectButtonProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<{ id: string; searchParams: URLSearchParams } | null>(null)
  const close = useCallback(() => setDialog(null), [])

  const open = () => {
    const url = newObjectUrl(type, context)
    if (type === 'creative') {
      navigate(url)
      return
    }
    const [path, query] = url.split('?')
    setDialog({ id: path.slice(path.lastIndexOf('/') + 1), searchParams: new URLSearchParams(query) })
  }

  return (
    <>
      <Button type="button" size={size} variant={variant} onClick={open}>
        <Plus className="size-4" /> {t(CONFIG[type].label)}
      </Button>
      {dialog && type !== 'creative' && (
        <CreateObjectDialog
          type={type}
          id={dialog.id}
          searchParams={dialog.searchParams}
          label={CONFIG[type].label}
          onClose={close}
        />
      )}
    </>
  )
}
